import type { IKeytipProps } from '@fluentui/react/lib/Keytip';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getTableControl as getTableControlFromActionComtrols } from 'owa-compose-actioncontrols';

import { getSignatureSections, AttachmentFlyoutSections, getAttachFileSections } from '../sections';
import { createAppFlyoutAnchor, getKeytipString, constructKeytip } from 'owa-command-ribbon';
import { getMainWindowComposeViewState } from 'owa-mail-compose-actions/lib/selectors/getMainWindowComposeViewState';
import { getIsButtonDisabled, getControlInfo } from 'owa-mail-ribbon-utils';
import type { MenuSection } from '@1js/acui-menu/lib/components/MenuDefinition';
import { computedFn } from 'mobx-utils/lib/computedFn';
import type { ComposeViewState } from 'owa-mail-compose-store';
import { getComposeRibbonId } from 'owa-editor/lib/utils/getComposeRibbonId';
import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { MailRibbonTabId, MailRibbonControlId } from 'owa-ribbon-ids';
import { getDefaultRibbonStyles } from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';
import isApprovalResponse from 'owa-mail-compose-view/lib/utils/isApprovalResponse';
import type AttachmentMultiPicker from 'owa-mail-compose-view/lib/types/AttachmentMultiPicker';
import type { AttachmentSuggestionsMenuItems } from 'owa-attachment-suggestions';
import { lazyGetAttachmentSuggestionsMenuItems } from 'owa-attachment-suggestions';
import {
    onShowLinkDialogClicked,
    onEmojisButtonClicked,
    onPicturesButtonClicked,
} from '../../util/commandActions';
import { createMailAppFlyoutAnchor, createMailAppButton } from '../../util/createMailRibbonButton';

export function getTableControl(
    popoutComposeViewState: ComposeViewState | undefined,
    targetWindow: Window
): RibbonControlProps {
    const controlInfo = getControlInfo(MailRibbonControlId.Table);
    const id: string | number = getComposeRibbonId(
        MailRibbonControlId.Table,
        popoutComposeViewState
    );
    const keytip: IKeytipProps | undefined = isFeatureEnabled('mon-ribbon-keytips')
        ? constructKeytip(
              [getKeytipString(MailRibbonTabId.Tab_Insert)],
              getKeytipString(MailRibbonControlId.Table),
              true /* hasMenu */
          )
        : undefined;
    const isPopout: boolean = popoutComposeViewState != undefined;
    const isButtonDisabled: boolean = getIsButtonDisabled(id, isPopout);
    const viewState: ComposeViewState | undefined =
        popoutComposeViewState ?? getMainWindowComposeViewState();
    return getTableControlFromActionComtrols(
        id,
        viewState,
        targetWindow,
        controlInfo.text,
        controlInfo.iconName,
        controlInfo.tooltip,
        keytip,
        isButtonDisabled
    );
}

export const getAttachFileControl = computedFn(
    (
        attachmentPickers: AttachmentMultiPicker,
        popoutComposeViewState: ComposeViewState | undefined,
        tab: MailRibbonTabId
    ): RibbonControlProps => {
        // Grab suggestionsGetter. Note that this isn't undefined, as getAttachFileControl only is provoked if
        // shouldShowRibbonControl returns true for AttachFile, which already checks that suggestionsGetter != undefined.
        const suggestionsGetter: AttachmentSuggestionsMenuItems = lazyGetAttachmentSuggestionsMenuItems.tryImportForRender()?.(
            true /* isMail */
        )!;

        const controlInfo = getControlInfo(MailRibbonControlId.AttachFile);
        const attachmentSections: AttachmentFlyoutSections = getAttachFileSections(
            suggestionsGetter,
            attachmentPickers,
            tab,
            popoutComposeViewState
        );

        const viewState: ComposeViewState | undefined =
            popoutComposeViewState ?? getMainWindowComposeViewState();

        return createMailAppFlyoutAnchor(
            getComposeRibbonId(MailRibbonControlId.AttachFile, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            { sections: attachmentSections.menuSections },
            constructKeytip(
                [getKeytipString(tab)],
                getKeytipString(MailRibbonControlId.AttachFile),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined /*iconColors*/,
            undefined /*Styles*/,
            {
                onExecute: attachmentSections.onMenuOpened,

                onAfterMenuDismiss: attachmentSections.onMenuDismissed,

                disabled:
                    attachmentSections.menuSections.length === 0 ||
                    !viewState ||
                    isApprovalResponse(viewState),
            }
        );
    }
);

export const getAttachSigControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        tab: MailRibbonTabId
    ): RibbonControlProps => {
        const signatureSections: MenuSection[] = getSignatureSections(
            tab,
            popoutComposeViewState ?? getMainWindowComposeViewState()!
        );
        const controlInfo = getControlInfo(MailRibbonControlId.AttachSig);
        return createAppFlyoutAnchor(
            getComposeRibbonId(MailRibbonControlId.AttachSig, popoutComposeViewState),
            controlInfo.text,
            {
                sections: signatureSections,
            },
            constructKeytip(
                [getKeytipString(tab)],
                getKeytipString(MailRibbonControlId.AttachSig),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined /*iconColors*/,
            getDefaultRibbonStyles(),
            {
                customTooltip: controlInfo.tooltip,
                disabled: signatureSections[0].controls.length == 0,
            }
        );
    }
);

export const getEmojisControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        targetWindow: Window
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Emojis);
        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.Emojis, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                const viewState: ComposeViewState | undefined =
                    popoutComposeViewState ?? getMainWindowComposeViewState();
                onEmojisButtonClicked(viewState, targetWindow);
            },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Insert)],
                getKeytipString(MailRibbonControlId.Emojis)
            ),
            controlInfo.iconName
        );
    }
);

export const getLinkControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        targetWindow: Window
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ShowLinkDialog);

        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.ShowLinkDialog, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                const viewState: ComposeViewState | undefined =
                    popoutComposeViewState ?? getMainWindowComposeViewState();

                if (viewState) {
                    onShowLinkDialogClicked(viewState, targetWindow);
                }
            },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Insert)],
                getKeytipString(MailRibbonControlId.ShowLinkDialog)
            ),
            controlInfo.iconName
        );
    }
);

export const getPicturesControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        attachmentPickers: AttachmentMultiPicker
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Pictures);
        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.Pictures, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                onPicturesButtonClicked(attachmentPickers);
            },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Insert)],
                getKeytipString(MailRibbonControlId.Pictures)
            ),
            controlInfo.iconName
        );
    }
);
